import { Formik } from 'formik'
import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  ContractorPortalUpdateEmail,
  ContractorPortalUpdateEmailSendCode,
} from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { ContractorPortalUpdateEmailEnterCodeModalValues } from './ContractorPortalUpdateEmailEnterCodeModal.interface'
import { ContractorPortalUpdateEmailEnterCodeModalSchema } from './ContractorPortalUpdateEmailEnterCodeModal.validator'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { Modal, Typography } from '@mui/material'
import { ChevronLeftMinor } from '@shopify/polaris-icons'
import { ResendCode } from 'src/components/shared/ResendCode'

interface Props {
  token: string
  email: string
  onClose: () => void
  onSuccess: () => void
}

const initialValues: ContractorPortalUpdateEmailEnterCodeModalValues = {
  code: '',
}

export const ContractorPortalUpdateEmailEnterCodeModal = ({
  token,
  email,
  onSuccess,
  onClose,
}: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const container = useMemo(() => document.getElementById('root'), [])

  const handleFormSubmit = async (values: ContractorPortalUpdateEmailEnterCodeModalValues) => {
    setIsLoading(true)
    setError(null)

    try {
      await createAsyncAction(
        dispatch,
        ContractorPortalUpdateEmail.request({ ...values, email, token }),
      )

      onSuccess()
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    try {
      await createAsyncAction(
        dispatch,
        ContractorPortalUpdateEmailSendCode.request({ email, token }),
      )
    } catch (err: any) {}
  }

  return (
    <Modal
      container={container}
      open
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
      disableScrollLock
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={ContractorPortalUpdateEmailEnterCodeModalSchema}
      >
        {({ handleSubmit }) => (
          <div css={tw`max-w-[590px] mx-4 flex-col p-4 pt-6 bg-shades-0 rounded-2xl`}>
            <Button
              css={tw`bg-primary-100 h-[42px] w-[82px] hover:bg-primary-200`}
              color="light"
              size="medium"
              leftIcon={<ChevronLeftMinor width={19} css={tw`fill-primary-500`} />}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Typography css={tw`mt-6`} variant="h2">
              Verify your email number
            </Typography>
            <Typography css={tw`text-shades-400 mt-2 mb-6 w-[326px]`}>
              {`We sent a code to your new email ${email}. Enter it below to confirm and save
              your new email address`}
            </Typography>
            <FormikTextField name="code" css={tw`w-full mb-6`} placeholder="XX-XX-XX" />
            <FormErrorMessage css={tw`mb-6`} error={error} />
            <Button className={tw`w-full mb-6`} loading={isLoading} onClick={() => handleSubmit()}>
              Continue
            </Button>
            <ResendCode onResend={handleResend} />
          </div>
        )}
      </Formik>
    </Modal>
  )
}
